import React, {useEffect, useRef, useState} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../store';
import ReCAPTCHA from "react-google-recaptcha";
import { BusinessDetailsFormData, BusinessSignUpFormData, PersonalInfoFormData, } from "../../util/interfaces";
import { restrictedCountries, defaultMessageObj, businessWaitlistGateKeys, TierOneTraits } from "../../util/constants";
import { StyledLink, FormCardHeader } from "styles/styled";
import styled from "styled-components";
import MessageCard from "../message-card";
import {Routes} from "../../routes";
import { Spacing } from "../../styles/constants";
import PersonalInfoForm from "./personalInfoForm";
import BusinessDetailsForm from "./businessDetailsForm";
import { AnimatePresence } from "framer-motion";
import { AuthEffects } from "../../store/auth";
import MotionDiv from "../helperComponents/motionDiv";


export const CustomerOption = styled.div `{
  margin: 0.25rem 1rem 0.25rem 0;
  display: flex;
}`;

export const Spacer = styled.div `{
  min-width: 30px;
}`;

export const FormCardH2 = styled(FormCardHeader)`
  font-size: 18px;
  line-height: 25px;
  margin-bottom: ${Spacing.Space3};
`;

const BusinessSignup = React.forwardRef(() => {

  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [page1Data, setPage1Data] = useState<BusinessSignUpFormData | {}>({});
  const signupErrorMessage = useSelector((state: RootState) => state.auth.signupErrorMessage);
  const accountErrorMessage = useSelector((state: RootState) => state.account.accountError);
  const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
  const urlExtension = useSelector((state: RootState) => state.app.urlExtension);
  const queryParams = useSelector((state: RootState) => state.app.queryParams);
  let recaptchaRef = useRef<ReCAPTCHA>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    if (signupErrorMessage && messageRef.current) {
      messageRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [signupErrorMessage]);

  const onSubmit: SubmitHandler<PersonalInfoFormData> = async (data: PersonalInfoFormData) => {
    const combinedData = {...data as PersonalInfoFormData, ...page1Data as BusinessDetailsFormData}
    let gCaptchaResponse: string | null = null;
    if(!captchaDisabled && recaptchaRef.current){
      gCaptchaResponse = await recaptchaRef.current.executeAsync()
      recaptchaRef.current.reset();
    }
    if(!restrictedCountries.includes(combinedData.country)){
      combinedData.marketingOptInStatus = undefined;
    }

    dispatch(AuthEffects.startBusinessSignUp({...combinedData, gCaptchaResponse, referrerId: queryParams.referrerId}));
  };

  const validateAndContinue: SubmitHandler<BusinessDetailsFormData> = async (data: BusinessDetailsFormData) => {
    setPage1Data(data);
    determineShowWaitlist(data);
    setCurrentPage(2);
  };

  let displayErrorMessage = null;
  if (accountErrorMessage) {
    displayErrorMessage = accountErrorMessage
  } else if (signupErrorMessage) {
    switch (signupErrorMessage) {
      case 'User already exists':
        displayErrorMessage =
          <span>This email address has already been used. Already have an account? <StyledLink aria-label="login"
                                                                                               role="link"
                                                                                               to={Routes.LOGIN + urlExtension}>Login</StyledLink>.</span>
        break;
      case 'Rate Limited':
        displayErrorMessage =
          <span>You have tried to sign up too many times in a short period of time. Please contact <a
            aria-label="email support" href="mailto:support@bitpay.com">support</a> if you're having trouble with our signup process.</span>;
        break;
      case 'Request failed with status code 429':
        displayErrorMessage =
          <span>You have tried to sign up too many times in a short period of time. Please contact <a
            aria-label="email support" href="mailto:support@bitpay.com">support</a> if you're having trouble with our signup process.</span>;
        break;
      default:
        displayErrorMessage = "An unexpected error occurred, please try again later."
        break;
    }
  }

  const determineShowWaitlist = (data: BusinessDetailsFormData) => {
    const { orgType, industry, country } = data;
    if (businessWaitlistGateKeys.includes(queryParams.apply)) {
      setShowWaitlist(false);
      return;
    }
    if (TierOneTraits.countries.includes(country) && TierOneTraits.industries.includes(industry) && TierOneTraits.orgTypes.includes(orgType)) {
      setShowWaitlist(false);
      return;
    }
    setShowWaitlist(true);
  };

  return (
      <>
      <div ref={messageRef}>
        <MessageCard type={defaultMessageObj.type}>
          {displayErrorMessage}
        </MessageCard>
      </div>
        <AnimatePresence exitBeforeEnter>
          <>
            {currentPage === 1 && (
              <MotionDiv
                key="businessDetails"
              >
                <BusinessDetailsForm validateAndContinue={validateAndContinue}/>
              </MotionDiv>
            )}
            {currentPage === 2 && (
              <MotionDiv
                key="PersonalInfo"
              >
              <PersonalInfoForm onSubmit={onSubmit} recaptchaRef={recaptchaRef} showWaitlist={showWaitlist}/>
              </MotionDiv>
            )}
          </>
        </AnimatePresence>
      </>
  )
});

export default BusinessSignup;
